import { useEffect, useMemo, useState } from 'react';
import { STATS_RANGES } from '@capforge/shared';
import type { StatsResponse } from '@capforge/shared';
import { api } from '../lib/api';
import type { StatsRange } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  formatBucketLabel,
  formatBucketTick,
  formatLatency,
  formatNumber,
  formatRatio,
  formatUsd,
} from '../lib/format';
import { StatCard } from '../components/StatCard';
import { BarChart } from '../components/BarChart';
import type { StackedPoint, StackedSeries } from '../components/BarChart';
import { Badge } from '../components/Badge';
import type { BadgeTone } from '../components/Badge';
import { EmptyBox, ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

const RANGE_LABEL: Record<StatsRange, string> = {
  '24h': '24 gio',
  '7d': '7 ngay',
  '30d': '30 ngay',
};

/**
 * Thu tu chong: upstream -> noi bo -> that bai.
 * Xanh duong nam giua hai mau am nen cam va do khong bao gio cham nhau —
 * do la cap mau kho phan biet nhat trong bo, ke ca voi mat thuong.
 */
const SERIES: StackedSeries[] = [
  { key: 'upstream', label: 'Upstream', color: 'var(--viz-upstream)' },
  { key: 'local', label: 'Giải nội bộ', color: 'var(--viz-local)' },
  { key: 'failed', label: 'Thất bại', color: 'var(--viz-failed)' },
];

/** Tu dong lam moi 30s: du nhanh de theo doi tai, du cham de khong dap DB. */
const AUTO_REFRESH_MS = 30_000;

function alertTone(level: 'info' | 'warn' | 'error'): BadgeTone {
  return level === 'error' ? 'danger' : level === 'warn' ? 'warn' : 'info';
}

export function Overview() {
  const [range, setRange] = useState<StatsRange>('24h');
  const [apiKeyId, setApiKeyId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const stats = useAsync<StatsResponse>(
    (signal) => api.stats(range, apiKeyId, signal),
    [range, apiKeyId],
  );
  const keys = useAsync((signal) => api.keys(signal), []);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => stats.reload(), AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [autoRefresh, stats.reload]);

  const data = stats.data;
  const hourly = range === '24h';

  const points = useMemo<StackedPoint[]>(() => {
    if (data === null) return [];
    return data.buckets.map((bucket) => ({
      label: formatBucketLabel(bucket.t, hourly),
      tick: formatBucketTick(bucket.t, hourly),
      values: [bucket.upstream, bucket.local, bucket.failed],
    }));
  }, [data, hourly]);

  const maxTaskTypeCount = useMemo(() => {
    if (data === null || data.byTaskType.length === 0) return 0;
    return Math.max(...data.byTaskType.map((row) => row.count));
  }, [data]);

  const upstreamOff = data !== null && data.estimatedSavingsUsd === null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Tổng quan</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {data === null
              ? 'Đang tải…'
              : `Cap nhat luc ${new Date(data.generatedAt).toLocaleTimeString('vi-VN', { hour12: false })}`}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="cf-label" htmlFor="range">
              Khoang
            </label>
            <select
              id="range"
              className="cf-select"
              value={range}
              onChange={(event) => {
                const value = event.target.value;
                const found = STATS_RANGES.find((r) => r === value);
                if (found !== undefined) setRange(found);
              }}
            >
              {STATS_RANGES.map((r) => (
                <option key={r} value={r}>
                  {RANGE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="cf-label" htmlFor="apikey">
              Project (API key)
            </label>
            <select
              id="apikey"
              className="cf-select"
              value={apiKeyId === null ? '' : String(apiKeyId)}
              onChange={(event) => {
                const value = event.target.value;
                setApiKeyId(value === '' ? null : Number(value));
              }}
            >
              <option value="">Tất cả</option>
              {(keys.data ?? []).map((key) => (
                <option key={key.id} value={key.id}>
                  {key.label}
                </option>
              ))}
            </select>
          </div>

          <label className="flex h-[34px] items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
            />
            Tu dong 30s
          </label>

          <button type="button" className="cf-btn cf-btn-ghost" onClick={() => stats.reload()}>
            Lam moi
          </button>
        </div>
      </div>

      {stats.error !== null && <ErrorBox message={stats.error} onRetry={stats.reload} />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Request 24 giờ"
          loading={stats.loading && data === null}
          value={formatNumber(data?.totals.requests24h)}
          hint={
            data === null
              ? undefined
              : `Tong trong ${RANGE_LABEL[data.range]}: ${formatNumber(data.totals.requests)} · dang chay ${formatNumber(data.totals.processing)}`
          }
        />

        <StatCard
          label="Tỉ lệ giải nội bộ"
          loading={stats.loading && data === null}
          value={formatRatio(data?.totals.localRate)}
          hint={
            data === null
              ? undefined
              : `${formatNumber(data.totals.localSolved)} noi bo · ${formatNumber(data.totals.upstreamSolved)} upstream · ${formatNumber(data.totals.failed)} that bai`
          }
        />

        <StatCard
          label="Độ trễ nội bộ p50 / p95"
          loading={stats.loading && data === null}
          value={
            data === null
              ? '—'
              : `${formatLatency(data.latency.localP50)} / ${formatLatency(data.latency.localP95)}`
          }
          hint={
            data === null ? undefined : data.latency.upstreamP50 === null ? (
              'Chưa có dữ liệu upstream để đối chiếu'
            ) : (
              <>
                Upstream: {formatLatency(data.latency.upstreamP50)} /{' '}
                {formatLatency(data.latency.upstreamP95)}
              </>
            )
          }
        />

        <StatCard
          label="Tiền tiết kiệm ước tính"
          loading={stats.loading && data === null}
          unavailable={upstreamOff}
          unavailableText="khong ap dung"
          value={formatUsd(data?.estimatedSavingsUsd)}
          hint={
            upstreamOff
              ? 'Upstream đang TẮT nên không có đơn giá để so sánh. Bật upstream nếu muốn thấy con số này.'
              : 'Ước tính theo số request đã được solver nội bộ giải thay vì mua ngoài.'
          }
        />
      </div>

      <section className="cf-card p-4">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold">
            {hourly ? 'Lưu lượng theo giờ' : 'Lưu lượng theo ngày'}
          </h2>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Ro chuot vao cot de xem so lieu
          </span>
        </div>
        {stats.loading && data === null ? (
          <div className="flex justify-center p-10">
            <Spinner size={22} label="Đang tải biểu đồ" />
          </div>
        ) : (
          <BarChart series={SERIES} points={points} height={230} />
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="cf-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Cảnh báo</h2>
          {data === null ? (
            <Spinner label="Đang tải" />
          ) : data.alerts.length === 0 ? (
            <EmptyBox
              message="Không có cảnh báo"
              hint="Profile bị circuit breaker hoặc tỉ lệ miss tăng đột biến sẽ hiện ở đây."
            />
          ) : (
            <ul className="space-y-2">
              {data.alerts.map((alert, index) => (
                <li
                  key={`${alert.code}-${index}`}
                  className="flex items-start gap-2 rounded border border-slate-200 p-2 dark:border-slate-800"
                >
                  <Badge tone={alertTone(alert.level)}>{alert.level.toUpperCase()}</Badge>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 dark:text-slate-200">{alert.message}</p>
                    <p className="cf-mono text-xs text-slate-500 dark:text-slate-400">
                      {alert.code}
                      {alert.profileKey !== undefined && ` · profile ${alert.profileKey}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="cf-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Theo loại task</h2>
          {data === null ? (
            <Spinner label="Đang tải" />
          ) : data.byTaskType.length === 0 ? (
            <EmptyBox message="Chưa có task nào" />
          ) : (
            <ul className="space-y-1.5">
              {data.byTaskType.map((row) => (
                <li key={row.taskType} className="text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="cf-mono truncate">{row.taskType}</span>
                    <span className="shrink-0 font-medium">{formatNumber(row.count)}</span>
                  </div>
                  <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-sm bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-sm"
                      style={{
                        width: `${maxTaskTypeCount === 0 ? 0 : (row.count / maxTaskTypeCount) * 100}%`,
                        backgroundColor: 'var(--viz-local)',
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="cf-card p-4">
        <h2 className="mb-2 text-sm font-semibold">Phản hồi từ client</h2>
        {data === null ? (
          <Spinner label="Đang tải" />
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {formatNumber(data.totals.reportedCorrect)}
            </span>{' '}
            bao dung ·{' '}
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {formatNumber(data.totals.reportedIncorrect)}
            </span>{' '}
            bao sai. Day la nguon nhan sach nhat va mien phi — project goi API nen luon goi{' '}
            <code className="cf-mono">/reportCorrect</code> hoac{' '}
            <code className="cf-mono">/reportIncorrect</code> sau khi biet ket qua submit.
          </p>
        )}
      </section>
    </div>
  );
}
